/**
 * The money derivation behind `useQuoteSummary` — Protected Zones #5 and #6.
 *
 * This is the layer between the pricing engine and everything a customer is
 * ever shown: it takes the per-line totals `pricing.ts` produced and assembles
 * the order-level figure, applying the order charges, the lump-sum discount
 * and the GST gate in that order.
 *
 *   effectiveItemsTotal = max(itemsTotal, minOrderCharge)   // when it applies
 *   appliedDiscount     = min(lumpSumDiscount, effectiveItemsTotal)
 *   subtotal            = effectiveItemsTotal - appliedDiscount
 *                         + deliveryFee + cbdSurchargeFee
 *   gst                 = hasGst ? subtotal * gstRate : 0
 *   grandTotal          = subtotal + gst
 *
 * It lived inline in the `useQuoteSummary` hook until 2026-09-23 and was
 * therefore untestable: the 42 protected pricing tests pin `pricing.ts`'s pure
 * functions only, and would stay green through a botched change to any line
 * above — including the GST company gate. Extracted verbatim so it can be
 * pinned (backlog #53 item 4). The hook is now a thin wrapper that reads the
 * calculator's state and calls this.
 *
 * Three rules that must not be "simplified":
 *
 * - **The GST gate is by issuing company, never hardcoded** (Protected Zone
 *   #6). Only WSS Windows & Doors charges GST; Smart Renovation and Pinki
 *   Beans issue zero-GST quotes. Renderers read the derived `gst` / `hasGst`,
 *   they never re-decide it — and `finalTotalReverseMath` must be passed this
 *   same `hasGst` or its divisor strips a GST that was never added.
 * - **The discount is capped at `effectiveItemsTotal`**, so a lump-sum
 *   discount can zero the goods but never eat into the delivery fee or the CBD
 *   surcharge, and can never drive the subtotal negative.
 * - **An empty (or free) cart is $0 flat.** Every order charge is already
 *   gated on `itemsTotal > 0` inside `computeOrderCharges`; the subtotal
 *   short-circuit keeps a stray discount on an empty cart from producing a
 *   negative total.
 */
import { GLOBAL_SETTINGS } from '../data/settings';
import { computeOrderCharges, type OrderCharges } from './orderCharges';

/**
 * The one issuing company that charges GST. The other two entities
 * (`Smart Renovation`, `Pinki Beans`) issue zero-GST quotes — see
 * `src/data/companyTheme.ts` for the full list of issuers.
 */
export const GST_ISSUING_COMPANY = 'WSS Windows & Doors';

/** Protected Zone #6: the GST gate, in one place. */
export const chargesGst = (issuedCompany: string): boolean => issuedCompany === GST_ISSUING_COMPANY;

export interface QuoteSummaryTotals extends OrderCharges {
  itemsTotal: number;
  appliedDiscount: number;
  subtotal: number;
  hasGst: boolean;
  gst: number;
  grandTotal: number;
}

interface SummaryItem {
  product?: { exemptFromOrderCharges?: boolean } | null;
  source?: 'catalog' | 'adhoc';
}

/**
 * `itemsTotal` is passed in rather than recomputed: the caller has already run
 * the cart through `calcItemTotal`, and this layer must never second-guess the
 * pricing engine's per-line figures.
 */
export function computeQuoteSummary(params: {
  items: SummaryItem[];
  itemsTotal: number;
  cbdSurcharge: boolean;
  deliveryWaived: boolean;
  lumpSumDiscount: number;
  issuedCompany: string;
}): QuoteSummaryTotals {
  const { items, itemsTotal, cbdSurcharge, deliveryWaived, lumpSumDiscount, issuedCompany } =
    params;

  const { minChargeApplied, effectiveItemsTotal, deliveryFee, cbdSurchargeFee } =
    computeOrderCharges({ items, itemsTotal, cbdSurcharge, deliveryWaived });

  // Cap the discount at the goods: it may zero the items but never the fees,
  // and never drives the subtotal negative.
  const appliedDiscount = Math.min(lumpSumDiscount, effectiveItemsTotal);

  const subtotal =
    itemsTotal > 0 ? effectiveItemsTotal - appliedDiscount + deliveryFee + cbdSurchargeFee : 0;
  const hasGst = chargesGst(issuedCompany);
  const gst = hasGst ? subtotal * GLOBAL_SETTINGS.gstRate : 0;
  const grandTotal = subtotal + gst;

  return {
    itemsTotal,
    minChargeApplied,
    effectiveItemsTotal,
    deliveryFee,
    cbdSurchargeFee,
    appliedDiscount,
    subtotal,
    hasGst,
    gst,
    grandTotal,
  };
}
